import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import api from "@/lib/api";
import { eur } from "@/lib/sched";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, Clock, XCircle } from "lucide-react";

export default function WaitlistClaim() {
    const { token } = useParams();
    const { t, lang, setLang } = useI18n();
    const [offer, setOffer] = useState(null);
    const [state, setState] = useState("loading"); // loading|ready|booked|taken|expired|declined|notfound|ask_stay
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get(`/public/waitlist/offer/${token}`).then(({ data }) => {
            setOffer(data);
            const l = data.language || data.salon_language;
            if (l && ["nl", "en", "ro"].includes(l)) setLang(l);
            setState(data.status === "sent" ? "ready" : data.status === "accepted" ? "booked" : data.status === "invalidated" ? "taken" : data.status === "declined" ? "declined" : "expired");
        }).catch(() => setState("notfound"));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const accept = async () => {
        setBusy(true);
        try { await api.post(`/public/waitlist/offer/${token}/accept`); setState("booked"); }
        catch (e) { const c = e?.response?.data?.detail?.code; setState(c === "expired" ? "expired" : "taken"); }
        finally { setBusy(false); }
    };
    const decline = async (stay) => {
        setBusy(true);
        try { await api.post(`/public/waitlist/offer/${token}/decline`, { stay }); setState("declined"); }
        catch { setState("expired"); } finally { setBusy(false); }
    };

    const start = offer ? new Date(offer.start) : null;
    const dateStr = start?.toLocaleDateString(lang, { weekday: "long", day: "numeric", month: "long" });
    const timeStr = start ? `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}` : "";
    const exp = offer?.expires_at ? new Date(offer.expires_at) : null;

    return (
        <div className="min-h-screen bg-secondary/30">
            <header className="border-b border-border/60 bg-background/80 backdrop-blur-xl"><div className="mx-auto flex max-w-xl items-center justify-between px-6 py-4"><Logo /><LanguageSwitcher compact /></div></header>
            <div className="mx-auto max-w-xl px-6 py-10">
                {state === "loading" && <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>}
                {state === "notfound" && <Msg icon={XCircle} title={t("wl.pub.notFound")} />}
                {offer && state !== "loading" && state !== "notfound" && (
                    <div className="rounded-3xl border border-border/70 bg-card p-8" data-testid="claim-card">
                        {offer.salon_logo ? <img src={offer.salon_logo} alt="" className="mb-4 h-12 object-contain" /> : <p className="mb-4 font-serif text-xl font-bold">{offer.salon_name}</p>}
                        {state === "ready" && (
                            <>
                                <h1 className="font-serif text-3xl font-bold">{t("wl.pub.spot")}</h1>
                                <dl className="mt-6 space-y-3 text-sm">
                                    <Row k={t("wl.service")} v={offer.services.map((s) => s.name).join(" + ")} />
                                    <Row k={t("calendar.employee")} v={offer.employee} />
                                    <Row k={t("common.date")} v={dateStr} />
                                    <Row k={t("calendar.startTime")} v={timeStr} />
                                    <Row k={t("sched.total")} v={eur(offer.price)} />
                                </dl>
                                {exp && <p className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" />{t("wl.pub.reservedUntil")} {String(exp.getHours()).padStart(2, "0")}:{String(exp.getMinutes()).padStart(2, "0")}</p>}
                                <Button onClick={accept} disabled={busy} className="mt-6 w-full rounded-full" data-testid="claim-accept">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("wl.pub.confirm")}</Button>
                                <Button variant="ghost" onClick={() => setState("ask_stay")} disabled={busy} className="mt-2 w-full rounded-full text-muted-foreground" data-testid="claim-decline">{t("wl.pub.decline")}</Button>
                            </>
                        )}
                        {state === "ask_stay" && (
                            <div data-testid="claim-stay">
                                <h2 className="font-serif text-2xl font-bold">{t("wl.pub.stay")}</h2>
                                <div className="mt-6 flex gap-2">
                                    <Button onClick={() => decline(true)} disabled={busy} className="flex-1 rounded-full" data-testid="claim-stay-yes">{t("wl.pub.yes")}</Button>
                                    <Button variant="outline" onClick={() => decline(false)} disabled={busy} className="flex-1 rounded-full" data-testid="claim-stay-no">{t("wl.pub.no")}</Button>
                                </div>
                            </div>
                        )}
                        {state === "booked" && <Msg icon={CheckCircle2} title={t("wl.pub.bookedTitle")} sub={`${offer.services.map((s) => s.name).join(" + ")} · ${dateStr} · ${timeStr}`} ok testid="claim-booked" />}
                        {state === "taken" && <Msg icon={XCircle} title={t("wl.pub.taken")} testid="claim-taken" />}
                        {state === "expired" && <Msg icon={Clock} title={t("wl.pub.expired")} testid="claim-expired" />}
                        {state === "declined" && <Msg icon={CheckCircle2} title={t("wl.pub.declined")} ok testid="claim-declined" />}
                    </div>
                )}
            </div>
        </div>
    );
}

function Row({ k, v }) { return <div className="flex justify-between gap-4 border-b border-border/50 pb-2"><dt className="text-muted-foreground">{k}</dt><dd className="text-right font-semibold">{v}</dd></div>; }
function Msg({ icon: Icon, title, sub, ok, testid }) {
    return <div className="py-6 text-center" data-testid={testid}><Icon className={`mx-auto h-12 w-12 ${ok ? "text-accent" : "text-muted-foreground"}`} /><h2 className="mt-4 font-serif text-2xl font-bold">{title}</h2>{sub && <p className="mt-2 text-sm text-muted-foreground">{sub}</p>}</div>;
}
